import { useCallback, useEffect, useState } from 'react'
import { MapPinned, ShieldAlert, AlertCircle } from 'lucide-react'
import { useGetAddressHealth } from '../hooks/backend/drivers'
import { useCapabilities } from '../hooks/useCapabilities'
import { Spinner } from '../lib/shadcn/spinner'
import { Switch } from '../lib/shadcn/switch'
import { Label } from '../lib/shadcn/label'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../lib/shadcn/table'
import { Chip } from '../components/interviews/Chip'
import { EditAddressDialog } from '../components/drivers/EditAddressDialog'
import {
  type AddressHealthRow,
  addressHealthTone,
  addressHealthLabel,
  addressButtonLabel,
  listedAddress,
} from '../utils/addressHealth'

export default function DriverAddressHealth() {
  const { loading: accessLoading, canView, canEditDrivers, caption } = useCapabilities()
  const [onlyNeedsAttention, setOnlyNeedsAttention] = useState(true)

  const health = useGetAddressHealth()
  const trigger = health.trigger

  const load = useCallback(() => {
    trigger({ onlyNeedsAttention }, { skipCache: true })
  }, [trigger, onlyNeedsAttention])

  useEffect(() => {
    if (canView) load()
  }, [canView, load])

  const rows = (health.data as AddressHealthRow[] | undefined) ?? []
  const shownCount = rows.length

  if (accessLoading) {
    return (
      <div className="flex h-full items-center justify-center gap-2 text-sm text-muted-foreground">
        <Spinner className="h-4 w-4" /> Checking access...
      </div>
    )
  }

  if (!canView) {
    return (
      <div className="flex h-full items-center justify-center p-6">
        <div className="flex max-w-md flex-col items-center gap-3 rounded-lg border border-border bg-card p-8 text-center">
          <div className="flex h-12 w-12 items-center justify-center rounded-full bg-[hsl(var(--brand-soft))] text-[hsl(var(--brand-soft-foreground))]">
            <ShieldAlert className="h-6 w-6" />
          </div>
          <p className="text-sm font-medium text-foreground">
            Driver Address Health is available to admins only.
          </p>
          <p className="text-xs text-muted-foreground">
            Contact an admin if you need access to driver address data.
          </p>
        </div>
      </div>
    )
  }

  return (
    <div className="flex h-full min-h-0 flex-col bg-card">
      {/* Header */}
      <div className="border-b border-border px-4 py-3">
        <div className="flex flex-wrap items-center gap-3">
          <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-[hsl(var(--brand-soft))] text-[hsl(var(--brand-soft-foreground))]">
            <MapPinned className="h-5 w-5" />
          </div>
          <h1 className="text-lg font-semibold text-foreground">Driver Address Health</h1>
          <div className="ml-auto flex items-center gap-2 rounded-md border border-border bg-background px-3 py-1.5">
            <Switch
              id="only-needs-attention"
              checked={onlyNeedsAttention}
              onCheckedChange={setOnlyNeedsAttention}
            />
            <Label htmlFor="only-needs-attention" className="cursor-pointer text-xs font-normal">
              Only addresses needing attention
            </Label>
          </div>
        </div>
        <p className="mt-1 text-xs text-muted-foreground">{caption}</p>
        <p className="mt-2 text-sm font-medium text-foreground">
          {health.loading
            ? 'Loading...'
            : onlyNeedsAttention
              ? `${shownCount} driver${shownCount === 1 ? '' : 's'} need${shownCount === 1 ? 's' : ''} an address fixed`
              : `${shownCount} active driver${shownCount === 1 ? '' : 's'}`}
        </p>
      </div>

      {/* Table */}
      <div className="min-h-0 flex-1 overflow-y-auto">
        {health.error ? (
          <div className="m-4 flex items-start gap-2 rounded-md border border-destructive/30 bg-destructive/10 p-3 text-sm text-destructive">
            <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" />
            <span>{health.error}</span>
          </div>
        ) : health.loading && rows.length === 0 ? (
          <div className="flex items-center justify-center gap-2 p-10 text-sm text-muted-foreground">
            <Spinner className="h-4 w-4" /> Loading address health...
          </div>
        ) : rows.length === 0 ? (
          <div className="flex flex-col items-center justify-center gap-2 p-12 text-center text-sm text-muted-foreground">
            <MapPinned className="h-8 w-8 opacity-40" />
            {onlyNeedsAttention
              ? 'No driver addresses need attention right now.'
              : 'No driver address data yet.'}
          </div>
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Driver</TableHead>
                <TableHead>City</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>What to do</TableHead>
                <TableHead>Listed address</TableHead>
                <TableHead>Phone</TableHead>
                {canEditDrivers ? <TableHead className="text-right">Action</TableHead> : null}
              </TableRow>
            </TableHeader>
            <TableBody>
              {rows.map((r) => (
                <TableRow key={r.driver_id}>
                  <TableCell className="font-medium text-foreground">
                    {r.driver_name || 'Unnamed driver'}
                  </TableCell>
                  <TableCell className="text-muted-foreground">{r.city || '-'}</TableCell>
                  <TableCell>
                    <Chip tone={addressHealthTone(r.address_health)}>
                      {addressHealthLabel(r.address_health)}
                    </Chip>
                  </TableCell>
                  <TableCell className="max-w-md">
                    <span
                      className="block whitespace-normal text-sm text-muted-foreground"
                      title={r.attention_reason ?? undefined}
                    >
                      {r.attention_reason || 'No action needed.'}
                    </span>
                  </TableCell>
                  <TableCell className="max-w-xs">
                    <span className="block truncate text-muted-foreground">{listedAddress(r)}</span>
                  </TableCell>
                  <TableCell className="whitespace-nowrap font-mono text-xs tabular-nums text-muted-foreground">
                    {r.primary_phone || '-'}
                  </TableCell>
                  {canEditDrivers ? (
                    <TableCell className="text-right">
                      <div className="flex justify-end">
                        <EditAddressDialog
                          driverId={r.driver_id}
                          driverName={r.driver_name}
                          label={addressButtonLabel(r)}
                          onSaved={load}
                        />
                      </div>
                    </TableCell>
                  ) : null}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </div>
    </div>
  )
}
